import { Directive, forwardRef, input, InputSignal, effect, inject } from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';
import { NumberParserService } from '../../../services/number/number-parser.service';

import type { Type } from '@angular/core';
import type { AbstractControl, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';

export const MAX_VALUE_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<MaxDecimalValidator> => MaxDecimalValidator),
  multi: true
};

/**
 * Max Decimals Validator Directive
 *
 * Validates number input if user has typed more than x number of decimal points
 *
 * @example
 * <input type="number" [maxDecimals]="2" />
 */
@Directive({
  selector: `[maxDecimals][formControlName],[maxDecimals][formControl],[maxDecimals][ngModel],
             [tdWbMaxDecimals][formControlName],[tdWbMaxDecimals][formControl],[tdWbMaxDecimals][ngModel]`,
  providers: [MAX_VALUE_VALIDATOR]
})
export class MaxDecimalValidator implements Validator {
  // underlying input signals (alias preserves attribute binding)
  readonly maxSignal: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'maxDecimals' });
  readonly tdWbMaxSignal: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'tdWbMaxDecimals' });
  // override storage for programmatic assignments (tests, runtime setters)
  private _maxOverride?: number;
  private _tdWbMaxOverride?: number;
  // numeric property accessors for compatibility with existing tests/code
  get max(): number | undefined { return this._maxOverride ?? this.maxSignal(); }
  set max(v: number | undefined) {
    this._maxOverride = v;
    if (v !== undefined) {
      this.createValidator(v);
      if (this.validatorOnChange !== undefined) {
        this.validatorOnChange();
      }
    }
  }

  get tdWbMax(): number | undefined { return this._tdWbMaxOverride ?? this.tdWbMaxSignal(); }
  set tdWbMax(v: number | undefined) {
    this._tdWbMaxOverride = v;
    if (v !== undefined) {
      this.createValidator(v);
      if (this.validatorOnChange !== undefined) {
        this.validatorOnChange();
      }
    }
  }

  // neutral validator so validate() is safe before inputs initialize
  private validatorFn: ValidatorFn = () => null;
  private validatorOnChange?: () => void;
  private readonly numberParserService: NumberParserService = inject(NumberParserService);

  constructor() {
    effect(() => {
      const m = this.maxSignal() ?? this.tdWbMaxSignal();
      if (m !== undefined) {
        this.createValidator(m);
        if (this.validatorOnChange !== undefined) {
          this.validatorOnChange();
        }
      }
    });
  }

  /**
   * This static method allows for the reactive forms to directly access the validation function
   *
   * @param maxDecimalsValue
   * @param numberParserService
   * @returns
   */
  static maxDecimals(maxDecimalsValue: number, numberParserService: NumberParserService): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } => {
      if (control.value === undefined || control.value === null) {
        return null;
      }
      const value: number = typeof control.value === 'string' ? numberParserService.parse(control.value) : control.value;
      const invalidNumberOfDecimals = Number(value.toFixed(maxDecimalsValue)) !== value;
      if (invalidNumberOfDecimals) {
        return { decimals: value };
      }
      return null;
    };
  }

  validate(abstractControl: AbstractControl): ValidationErrors | null {
    return this.validatorFn(abstractControl);
  }

  registerOnValidatorChange(validatorOnChange: () => void): void {
    this.validatorOnChange = validatorOnChange;
  }

  private createValidator(maxDecimalsValue: number): void {
    this.validatorFn = MaxDecimalValidator.maxDecimals(maxDecimalsValue, this.numberParserService);
  }
}
